import { useRef, useEffect, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const cities = [
  'Santa Maria', 'San Luis Obispo', 'Santa Barbara', 'Lompoc',
  'Orcutt', 'Arroyo Grande', 'Pismo Beach', 'Grover Beach',
  'Nipomo', 'Guadalupe', 'Los Alamos', 'Buellton',
  'Solvang', 'Los Olivos', 'Santa Ynez', 'Casmalia',
  'Oceano', 'Goleta', 'Carpinteria', 'Ventura',
];

export default function ServiceAreas() {
  const { ref, inView } = useInView();

  return (
    <section id="service-areas" className="bg-[#0D0D0D] py-24 md:py-32" ref={ref}>
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 lg:px-16">

        {/* Header */}
        <div className={`mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-[2px] bg-[#C9A84C]"></div>
            <span className="text-[#C9A84C] text-[10px] tracking-[0.45em] uppercase font-medium">Where We Serve</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="font-serif text-white text-4xl md:text-5xl font-bold leading-[1.1]">
              Serving Clients Across<br />the Central Coast
            </h2>
            <p className="text-white/35 text-sm max-w-xs leading-relaxed md:text-right">
              Criminal defense, personal injury, family law, and civil litigation services throughout Santa Barbara and San Luis Obispo Counties.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

          {/* Left: Cities + Office cards */}
          <div className={`transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <h3 className="text-[#C9A84C] text-[10px] tracking-[0.38em] uppercase font-semibold mb-6">Service Locations</h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 mb-12">
              {cities.map((city, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-1 h-1 rounded-full bg-[#C9A84C] flex-shrink-0"></div>
                  <span className="text-white/55 text-sm">{city}</span>
                </div>
              ))}
            </div>

            {/* Office cards */}
            <div className="space-y-4">
              <div className="bg-[#141414] border border-[#C9A84C]/20 p-6 hover:border-[#C9A84C]/50 transition-colors duration-300 group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#C9A84C]/10 group-hover:bg-[#C9A84C]/20 transition-colors duration-300">
                    <i className="ri-building-2-line text-[#C9A84C] text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1.5">Santa Maria Office</h4>
                    <p className="text-white/40 text-xs leading-relaxed">
                      301 E. Cook Street, Suite E<br />
                      Santa Maria, California 93454-5133
                    </p>
                    <a href="tel:8059228308" className="text-[#C9A84C] text-xs mt-2.5 inline-flex items-center gap-1.5 hover:underline cursor-pointer">
                      <i className="ri-phone-fill text-xs"></i>
                      (805) 922-8308
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-[#141414] border border-[#C9A84C]/20 p-6 hover:border-[#C9A84C]/50 transition-colors duration-300 group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#C9A84C]/10 group-hover:bg-[#C9A84C]/20 transition-colors duration-300">
                    <i className="ri-building-2-line text-[#C9A84C] text-lg"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-sm mb-1.5">San Luis Obispo Office</h4>
                    <p className="text-white/40 text-xs leading-relaxed">
                      2146 Parker Street, Suite A-3<br />
                      San Luis Obispo, California 93401
                    </p>
                    <a href="tel:8059228308" className="text-[#C9A84C] text-xs mt-2.5 inline-flex items-center gap-1.5 hover:underline cursor-pointer">
                      <i className="ri-phone-fill text-xs"></i>
                      (805) 922-8308
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Map — correct coordinates for 301 E. Cook St, Santa Maria, CA */}
          <div className={`transition-all duration-700 delay-400 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="w-full h-[500px] overflow-hidden border border-[#C9A84C]/20">
              <iframe
                title="Gamble & Garcia Law Firm — 301 E. Cook Street, Santa Maria, CA"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3291.4!2d-120.4357!3d34.9530!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80ec5b5b5b5b5b5b%3A0x0!2s301+E+Cook+St+Suite+E%2C+Santa+Maria%2C+CA+93454!5e0!3m2!1sen!2sus!4v1700000000001!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(30%) contrast(1.1) brightness(0.9)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-2-fill text-[#C9A84C] text-sm"></i>
              </div>
              <p className="text-white/30 text-[11px] tracking-wider">
                Gamble &amp; Garcia, Attorneys at Law — 301 E. Cook Street, Suite E, Santa Maria, CA 93454
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
